import { Module, ActionContext } from 'vuex';
import { magic } from '@gig/magic-sdk';
import {
    ILoyaltyState,
    SET_USER_PROGRESS,
    SET_LOYALTY_LOADED,
    SET_LOYALTY_TIER,
    SET_REWARD_COUNT_LOADING,
    SET_REWARD_COUNT,
    SET_REWARDS,
    SET_USER_REWARDS,
} from './loyalty.constants';
import { ILoayaltyUserProgressResponse } from '@gig/magic-sdk/dist/adapter/loyalty/userProgress';
import { ILoyaltyPointsUpdateEvent } from '@gig/magic-sdk/dist/events';
import { IAppState } from '../../constants';
import { IReward } from '@gig/magic-sdk/dist/adapter/rewards/reward';

export const getters = {
    level(state: ILoyaltyState) {
        return state.level;
    },
    points(state: ILoyaltyState) {
        return state.points;
    },
    pointsNextLevel(state: ILoyaltyState) {
        return state.pointsNextLevel;
    },
    pointsPreviousLevel(state: ILoyaltyState) {
        return state.pointsPreviousLevel;
    },
    loyaltyLoaded(state: ILoyaltyState) {
        return state.loyaltyLoaded;
    },
    progress(state: ILoyaltyState) {
        return state.progress;
    },
    tier(state: ILoyaltyState) {
        return state.tier;
    },
    rewards: (state: ILoyaltyState) => state.rewards,
    userRewards: (state: ILoyaltyState) => state.userRewards,
    userRewardsEchoes: (state: ILoyaltyState) => state.userRewardsEchoes,
    rewardCount(state: ILoyaltyState) {
        return state.rewardsCount;
    },
    reward: (state: ILoyaltyState) => (slug: string) => {
        return state.rewards[slug]
    },
};

export const actions = {
    async fetchUserProgress(
        { commit }: ActionContext<ILoyaltyState, IAppState>,
        {
            userId,
        }: {
            userId: number;
        },
    ) {
        commit(SET_LOYALTY_LOADED, false);
        const payload = await magic.methods.loyalty.userProgress({
            userId,
        });
        commit(SET_USER_PROGRESS, payload);
        commit(SET_LOYALTY_LOADED, true);
    },
    updateUserProgress(
        { commit }: ActionContext<ILoyaltyState, IAppState>,
        {
            updatedProgress,
        }: {
            updatedProgress: ILoyaltyPointsUpdateEvent
        }
    ){
        commit(SET_LOYALTY_LOADED, false);
        commit(SET_USER_PROGRESS, updatedProgress);
        commit(SET_LOYALTY_LOADED, true);
    },
    setLoyaltyTier(
        {commit}: ActionContext<ILoyaltyState, IAppState>,
        {
            tier,
        }: {
            tier: number;
        }
    ) {
        commit(SET_LOYALTY_TIER, tier);
    },
    async fetchRewards(
        { commit, state }: ActionContext<ILoyaltyState, IAppState>,
        {
            userName,
            cache = true,
        }: {
            userName: string;
            cache?: boolean;
        },
    ) {
        if (cache && Object.keys(state.rewards).length > 0) {
            return;
        }
        const payload = await magic.methods.rewards.getAll({
            userName,
            order: 'daysValid',
        });
        if (payload) {
            commit(SET_REWARDS, payload);
        }
    },
    async fetchUserRewards({ commit }: ActionContext<ILoyaltyState, IAppState>) {
        const payload = await magic.methods.rewards.getUserRewards({
            order: 'asc',
        });
        commit(SET_USER_REWARDS, payload);
    },
    async fetchRewardCount({
        commit,
        state,
    }: ActionContext<ILoyaltyState, IAppState>) {
        if (state.isLoading) {
            return;
        }

        try {
            commit(SET_REWARD_COUNT_LOADING, true);
            const amount = await magic.methods.rewards.newlyAwardedTokens();

            commit(SET_REWARD_COUNT, amount);
        } catch (e) {
            // Do not notify the error to avoid confusing the user as
            // loading the reward count is not explicitly requested by the user
            console.error(e);
        } finally {
            commit(SET_REWARD_COUNT_LOADING, false);
        }
    },
    async claimReward(
        { commit, state }: ActionContext<ILoyaltyState, IAppState>,
        {
            reward,
            userName,
        }: {
            reward: IReward;
            userName: string;
        },
    ) {
        try {
            const payload = await magic.methods.rewards.claimReward({
                rewardName: reward.slug,
                claimAll: false,
            });
            if (
                payload.rewards[userName] &&
                payload.rewards[userName].rewards
            ) {
                if (payload.rewardsWon) {
                    // prevent flickering due to auto-exchanged won rewards when claiming ranged rewards
                    const wonAutoExchangedRewards = payload.rewardsWon.filter(
                        (rewardWon) =>
                            state.rewards[rewardWon.reward].autoExchange,
                    );

                    for (const rewardWon of wonAutoExchangedRewards) {
                        delete payload.rewards[userName].rewards[
                            rewardWon.reward
                        ];
                    }
                }

                commit(SET_USER_REWARDS, payload.rewards[userName]);
            }

            return payload;
        } catch (e) {
            console.error('Failed to claim', reward.slug, e);
            return false;
        }
    },
    clearRewardCount({ commit }: ActionContext<ILoyaltyState, IAppState>) {
        commit(SET_REWARD_COUNT, 0);
    },
};

export const loyalty: Module<ILoyaltyState, IAppState> = {
    namespaced: true,
    state: {
        rewards: {},
        userRewards: null,
        userRewardsEchoes: 0,
        rewardsCount: 0,
        isLoading: false,
        level: null,
        points: null,
        pointsNextLevel: null,
        pointsPreviousLevel: null,
        progress: null,
        loyaltyLoaded: false,
        tier: null,
    },
    getters,
    actions,
    mutations: {
        [SET_USER_PROGRESS](
            state,
            payload: ILoayaltyUserProgressResponse
        ) {
            state.level = payload.level;
            state.points = payload.points;
            state.pointsNextLevel = payload.pointsNextLevel;
            state.pointsPreviousLevel = payload.pointsPreviousLevel;
            state.progress = payload.progress;
        },
        [SET_LOYALTY_LOADED](state, payload: boolean) {
            state.loyaltyLoaded = payload;
        },
        [SET_LOYALTY_TIER](state, payload: number) {
            state.tier = payload;
        },
        [SET_REWARD_COUNT_LOADING](state, payload: boolean) {
            state.isLoading = payload;
        },
        [SET_REWARD_COUNT](state, payload: number) {
            state.rewardsCount = payload;
        },
        [SET_REWARDS](state, payload) {
            /**
             * Normalize the rewards using reward.slug as unique key
             */
            state.rewards = payload.reduce(
                (acc: Record<string, IReward>, reward: IReward) => {
                    acc[reward.slug] = reward;
                    return acc;
                },
                {},
            );
        },
        [SET_USER_REWARDS](state, payload) {
            if (payload.rewards.echoes) {
                state.userRewardsEchoes = payload.rewards.echoes;
            }

            /**
             * delete user rewards that are null
             */
            Object.keys(payload.rewards).forEach((slug) => {
                if (!payload.rewards[slug]) {
                    delete payload.rewards[slug];
                }
            });

            state.userRewards = payload.rewards;
        },
    },
};
